import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";
import {
  fetchBusinessTypes,
  fetchBusinessTypesFrontend,
  getSavedFilters,
} from "../../apis";
import { authAtom } from "../auth";
import { apiServer } from "../../config";
import { atomWithLocalStorage, Storage } from "../../storage";
import { fetchWithToken } from "../../apis/fetchWithToken";

export interface BusinessType {
  image: string;
  name: string;
}

type FiltersApplied = {
  types: string;
  accessibilities: string;
  showVenueOptions: string;
} | null;

export interface BusinessTypeFrontend {
  image: string;
  name: string;
}
interface FilterState {
  openingHoursSelected: boolean;
  accessibilityOptions: number[];
  isFiltersApplied: boolean;
  filtersApplied: FiltersApplied;
  selectedBusinessTypes: string[];
  selectedFiltersNames: string[];
}

export type OpeningHoursType = "any" | "openNow" | "custom";
export type TimeValue = {
  hour: number;
  minute: number;
  period: "AM" | "PM";
};
export interface VenueFilterState {
  openingHoursType: OpeningHoursType;
  openingHours?: TimeValue;
  openingHoursDay?: number;
  accessibilityOptions: number[];
  isFiltersApplied: boolean;
  filtersApplied: FiltersApplied;
  selectedBusinessTypes: string[];
  selectedFiltersNames: string[];
}

export const businessTypesAtom = atomWithQuery(() => ({
  queryKey: ["businessTypes"],
  queryFn: async () => {
    const response = await fetchBusinessTypes();
    const data = await response.json();
    return data.data as BusinessType[];
  },
}));

export const businessTypesFrontendAtom = atomWithQuery(() => ({
  queryKey: ["businessTypesFrontend"],
  queryFn: async () => {
    const response = await fetchBusinessTypesFrontend();
    const data = await response.json();
    return data.data as BusinessTypeFrontend[];
  },
}));

export const userAccessibilityPreferencesAtom = atomWithQuery((get) => ({
  queryKey: ["userAccessibilityPreferences", get(authAtom)?.data?.accessToken],
  queryFn: async ({ queryKey }) => {
    const accessToken = queryKey[1] as string | null | undefined;
    if (!accessToken) {
      console.log(`userAccessibilityPreferences called without token`);
      return [];
    }
    if (accessToken.length <= 0) {
      console.log(`userAccessibilityPreferences called without token`);
      return [];
    }

    console.log(`userAccessibilityPreferences called with token`);

    const response = await fetchWithToken(
      apiServer + "/user/sync-user-accessibilty-preferences",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const data = await response?.json();
    return data.data.accessibility_preferences as any[];
  },
}));

export const savedFiltersAtom = atomWithQuery((get) => ({
  queryKey: ["savedFilters", Storage.getCurrentUserToken()],
  queryFn: async ({ queryKey }) => {
    const accessToken = Storage.getCurrentUserToken();
    if (!accessToken) {
      console.log(`savedFilters called without token`);
      return null;
    }
    if (accessToken.length <= 0) {
      console.log(`savedFilters called without token`);
      return null;
    }
    const data = await getSavedFilters(accessToken);
    return data;
  },
  enabled: false,
}));

export const venueFilterStateAtom = atom<VenueFilterState>({
  openingHoursType: "any",
  openingHours: undefined,
  openingHoursDay: undefined,
  accessibilityOptions: [],
  isFiltersApplied: false,
  filtersApplied: null,
  selectedBusinessTypes: [],
  selectedFiltersNames: [],
});

export const filterStateAtom = atom<FilterState>({
  openingHoursSelected: false,
  accessibilityOptions: [],
  isFiltersApplied: false,
  filtersApplied: null,
  selectedBusinessTypes: [],
  selectedFiltersNames: [],
});

export const tagFilterStateAtom = atom<FilterState>({
  openingHoursSelected: false,
  accessibilityOptions: [],
  isFiltersApplied: false,
  filtersApplied: null,
  selectedBusinessTypes: [],
  selectedFiltersNames: [],
});

export const savedFiltersStateAtom = atomWithLocalStorage<{
  accessibilityOptions: number[];
}>("savedFiltersStateAtom", { accessibilityOptions: [] });
